<template>
  <section class="content-header">
    <h1>Baremos facturados</h1>
  </section>

  <section class="content">
    <div class="box box-primary">
      <div class="box-body">
        <div class="row">
          <div class="col-md-3">
            <div class="form-group">
              <label for="compania">Compañía</label>
              <Dropdown v-model="companyia" inputId="compania"
                :options="companyias" optionLabel="nombre" optionValue="id"
                filter placeholder="Todas las compañías" class="w-100" />
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-group">
              <label for="operario">Operarios</label>
              <MultiSelect v-model="operariosSeleccionados" inputId="operario"
                :options="operarios" optionLabel="nombre" optionValue="id"
                filter display="chip" :maxSelectedLabels="2"
                selectedItemsLabel="{0} operarios seleccionados"
                placeholder="Todos los operarios"
                scrollHeight="400px" class="w-100" />
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-group">
              <label for="proveedor">Proveedores</label>
              <MultiSelect v-model="proveedoresSeleccionados" inputId="proveedor"
                :options="proveedores" optionLabel="nombre_proveedor" optionValue="id"
                filter display="chip" :maxSelectedLabels="2"
                selectedItemsLabel="{0} proveedores seleccionados"
                placeholder="Todos los proveedores"
                scrollHeight="400px" class="w-100" />
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-group">
              <label for="especialidad">Especialidades</label>
              <MultiSelect v-model="especialidadesSeleccionadas" inputId="especialidad"
                :options="especialidades" optionLabel="nombre" optionValue="id"
                filter display="chip" :maxSelectedLabels="2"
                selectedItemsLabel="{0} especialidades seleccionadas"
                placeholder="Todas las especialidades"
                scrollHeight="400px" class="w-100" />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-3">
            <div class="form-group">
              <label for="destinatario">Destinatario</label>
              <Dropdown v-model="destinatario" inputId="destinatario"
                :options="destinatarios" optionLabel="nombre" optionValue="valor"
                class="w-100" />
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-group">
              <label for="tipo_fecha">Intervalo de</label>
              <Dropdown v-model="tipoFecha" inputId="tipo_fecha"
                :options="tiposFecha" optionLabel="nombre" optionValue="valor"
                class="w-100" />
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-group">
              <label for="fecha_inicio">Fecha inicio</label>
              <Calendar v-model="fechaInicio" inputId="fecha_inicio"
                dateFormat="dd/mm/yy" showIcon class="w-100" />
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-group">
              <label for="fecha_fin">Fecha fin</label>
              <Calendar v-model="fechaFin" inputId="fecha_fin"
                dateFormat="dd/mm/yy" showIcon class="w-100" />
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-group">
              <label for="codigo">Código/s (;)</label>
              <InputText v-model="codigo" inputId="codigo" class="w-100" />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-8">
            <div class="botonera">
              <button class="btn btn-default" @click="mostrarEnPantalla" :disabled="generando">
                <i class="fa" :class="generando === 'pantalla' ? 'fa-spinner fa-spin' : 'fa-desktop'"></i>
                Mostrar en pantalla
              </button>
              <button class="btn btn-primary" @click="descargarInforme('pdf')" :disabled="generando">
                <i class="fa" :class="generando === 'pdf' ? 'fa-spinner fa-spin' : 'fa-file-pdf-o'"></i>
                Generar PDF
              </button>
              <button class="btn btn-success" @click="descargarInforme('xls')" :disabled="generando">
                <i class="fa" :class="generando === 'xls' ? 'fa-spinner fa-spin' : 'fa-file-excel-o'"></i>
                Generar Excel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="box box-primary" v-if="resultados !== null">
      <div class="box-header with-border">
        <h3 class="box-title">Listado de códigos</h3>
      </div>
      <div class="box-body">
        <DataTable :value="resultados" paginator :rows="25"
          :rowsPerPageOptions="[25, 50, 100]" sortMode="multiple"
          removableSort stripedRows size="small" scrollable
          responsiveLayout="scroll">
          <template #empty>No se han encontrado códigos con estos filtros.</template>

          <Column field="especialidad" header="Especialidad" sortable />
          <Column field="codigo" header="Código" sortable />
          <Column field="descripcion" header="Descripción" sortable />
          <Column field="destinatario" header="Destinatario" sortable />

          <Column field="cantidad_codigo" header="Cant./Exp." sortable>
            <template #body="{ data }">
              {{ data.cantidad_codigo }} ({{ data.porcentaje_codigo }}%)
            </template>
          </Column>

          <Column field="cantidad" header="Cant./Cód." sortable />

          <Column field="precio" header="Importe compañía" sortable>
            <template #body="{ data }">{{ formatearImporte(data.precio) }}</template>
          </Column>

          <Column field="precio_operario" header="Importe operario" sortable>
            <template #body="{ data }">{{ formatearImporte(data.precio_operario) }}</template>
          </Column>

          <Column field="rentabilidad" header="Rentabilidad" sortable>
            <template #body="{ data }">
              {{ formatearImporte(data.rentabilidad) }} ({{ data.porcentaje_rentabilidad }}%)
            </template>
          </Column>

          <ColumnGroup type="footer" v-if="totales">
            <Row>
              <Column footer="Totales:" :colspan="4" footerStyle="text-align:right" />
              <Column :footer="String(totales.cantidad_codigo)" />
              <Column :footer="String(totales.cantidad)" />
              <Column :footer="formatearImporte(totales.precio)" />
              <Column :footer="formatearImporte(totales.precio_operario)" />
              <Column :footer="formatearImporte(totales.rentabilidad) + ' (' + totales.porcentaje_rentabilidad + '%)'" />
            </Row>
          </ColumnGroup>
        </DataTable>
      </div>
    </div>

    <form id="FormInforme" method="post" style="display:none"></form>
    <iframe name="iframeInforme" style="display:none"></iframe>
  </section>
</template>

<script>
import { PwgsApi } from '../../../services/PwgsApi';
import MultiSelect from 'primevue/multiselect';
import Dropdown from 'primevue/dropdown';
import Calendar from 'primevue/calendar';
import InputText from 'primevue/inputtext';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';

export default {
  components: { MultiSelect, Dropdown, Calendar, InputText, DataTable, Column, ColumnGroup, Row },

  data() {
    const hoy = new Date();
    return {
      companyias: [],
      companyia: '',
      operarios: [],
      operariosSeleccionados: [],
      proveedores: [],
      proveedoresSeleccionados: [],
      especialidades: [],
      especialidadesSeleccionadas: [],
      destinatario: '',
      tipoFecha: 'fecha_subsanado',
      codigo: '',
      fechaInicio: new Date(hoy.getFullYear(), hoy.getMonth(), 1),
      fechaFin: hoy,
      generando: false,

      resultados: null,
      totales: null,

      destinatarios: [
        { valor: '', nombre: 'Todos' },
        { valor: 'compania', nombre: 'Compañía' },
        { valor: 'operario', nombre: 'Operario' }
      ],
      tiposFecha: [
        { valor: 'fecha_subsanado', nombre: 'Fecha subsanado' },
        { valor: 'fecha_factura', nombre: 'Fecha factura' },
        { valor: 'fecha_cobro', nombre: 'Fecha cobro' }
      ]
    };
  },

  methods: {
    formatearFecha(fecha) {
      if (!fecha) return '';
      const d = String(fecha.getDate()).padStart(2, '0');
      const m = String(fecha.getMonth() + 1).padStart(2, '0');
      return `${d}/${m}/${fecha.getFullYear()}`;
    },

    formatearImporte(valor) {
      const n = Number(valor) || 0;
      return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // Los multiselect viajan como arrays: codigosBaremoFechas() hace implode
    cuerpoFiltros(tipoRespuesta) {
      return {
        tipo_respuesta: tipoRespuesta,
        id_companyia: this.companyia,
        id_operario: this.operariosSeleccionados,
        id_proveedor: this.proveedoresSeleccionados,
        id_especialidad: this.especialidadesSeleccionadas,
        fecha_inicio: this.formatearFecha(this.fechaInicio),
        fecha_fin: this.formatearFecha(this.fechaFin),
        codigo: this.codigo,
        destinatario: this.destinatario,
        tipo_fecha: this.tipoFecha
      };
    },

    async mostrarEnPantalla() {
      this.generando = 'pantalla';
      try {
        const api = new PwgsApi();
        const respuesta = await api.post('informes/informe_baremos_facturados', this.cuerpoFiltros('json'));

        const informe = respuesta.informes || respuesta;
        this.resultados = informe.datos || [];
        this.totales = informe.totales || null;
      } finally {
        this.generando = false;
      }
    },

    // PDF y Excel siguen yendo contra el fichero antiguo
    async descargarInforme(tipo) {
      const destino = tipo === 'pdf' ? 'VentanaInforme' : 'iframeInforme';
      if (tipo === 'pdf') window.open('', 'VentanaInforme');

      this.generando = tipo;
      try {
        let direccion = localStorage.getItem('direccion');
        if (direccion == 'null.pwgs.app') direccion = 'pwgs5.local';

        const form = document.getElementById('FormInforme');
        form.innerHTML = '';
        form.method = 'post';
        form.target = destino;
        form.action = 'https://' + direccion + '/informe_baremos_facturados_doc.php?doc=' +
          (tipo === 'pdf' ? 'pdf' : 'xls');

        const anyadir = (nombre, valor) => {
          const i = document.createElement('input');
          i.type = 'hidden';
          i.name = nombre;
          i.value = valor;
          form.appendChild(i);
        };

        anyadir('destinatario', this.destinatario);
        anyadir('id_compania', this.companyia);
        anyadir('codigo', this.codigo);
        anyadir('tipo_fecha', this.tipoFecha);
        anyadir('fecha_inicio', this.formatearFecha(this.fechaInicio));
        anyadir('fecha_fin', this.formatearFecha(this.fechaFin));

        const anyadirLista = (nombre, lista) => {
          if (lista.length) {
            lista.forEach(id => anyadir(nombre, id));
          } else {
            anyadir(nombre, '');
          }
        };

        anyadirLista('id_operario[]', this.operariosSeleccionados);
        anyadirLista('id_proveedor[]', this.proveedoresSeleccionados);
        anyadirLista('id_especialidad[]', this.especialidadesSeleccionadas);

        anyadir('Generar', tipo === 'pdf' ? 'Generar documento PDF' : 'Generar documento Excel');

        form.submit();
      } finally {
        this.generando = false;
      }
    },

    async cargarCompanyias() {
      const api = new PwgsApi();
      const resp = await api.get('companias/simple?sortField=nombre&sortOrder=1');
      this.companyias = [{ id: '', nombre: 'Todas las compañías' }].concat(resp.datos);
    },

    async cargarOperarios() {
      const api = new PwgsApi();
      const resp = await api.get('operarios/simple');
      this.operarios = resp.datos;
    },

    async cargarEspecialidades() {
      const api = new PwgsApi();
      const resp = await api.get('especialidades');
      this.especialidades = resp.datos;
    },

    async cargarProveedores() {
      const api = new PwgsApi();
      const resp = await api.get('proveedores/?sortField=nombre_proveedor&sortOrder=1');
      this.proveedores = resp.datos;
    }
  },

  mounted() {
    this.cargarCompanyias();
    this.cargarOperarios();
    this.cargarEspecialidades();
    this.cargarProveedores();
  }
};
</script>

<style scoped>
.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 5px;
}

.w-100 :deep(.p-multiselect),
.w-100 :deep(.p-dropdown),
.w-100 :deep(.p-calendar),
.w-100 :deep(.p-inputtext) {
  width: 100%;
}

.botonera {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>